import { useEffect, useState } from "react";
import axios from "axios";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ClipboardList, TrendingUp, DollarSign, Calendar, ArrowRight, PlusCircle, AlertCircle, ExternalLink, Wrench, Truck, AlertTriangle, Clock, ScanLine, Plus } from "lucide-react";
import { StatusBadge } from "@/components/StatusPipeline";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAY_STYLES = {
  "Unpaid":  "bg-rose-50 text-rose-700 border border-rose-200",
  "Partial": "bg-amber-50 text-amber-700 border border-amber-200",
  "Paid":    "bg-emerald-50 text-emerald-700 border border-emerald-200",
};
const PayBadge = ({ s }) => <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${PAY_STYLES[s]||"bg-slate-100"}`}>{s}</span>;
const fmtDate  = d => d ? new Date(d).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}) : "";

const KPICard = ({ title, value, icon: Icon, iconBg, testId, loading }) => (
  <Card className="rounded-2xl border border-slate-200/70 bg-white hover:shadow-md transition-shadow" data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{title}</span>
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${iconBg}`}><Icon size={17}/></div>
      </div>
      {loading ? <Skeleton className="h-8 w-20" /> :
        <p className="text-2xl font-semibold tabular-nums text-slate-900" style={{fontFamily:"'Space Grotesk',sans-serif"}}>{value}</p>}
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const [stats,  setStats]  = useState(null);
  const [loading,setLoading]= useState(true);
  const [error,  setError]  = useState(null);
  const [overdue,setOverdue]= useState([]);
  const [overdueConfig, setOverdueConfig] = useState(3);
  const [scStats, setScStats] = useState(null);
  const [scLoading, setScLoading] = useState(true);
  useEffect(() => {
    api.get(`${API}/dashboard`)
      .then(r => setStats(r.data))
      .catch(() => setError("Failed to load dashboard"))
      .finally(() => setLoading(false));
    api.get(`${API}/alerts/overdue`)
      .then(r => { setOverdue(r.data.overdue || []); setOverdueConfig(r.data.overdue_days || 3); })
      .catch(() => {});
    api.get(`${API}/dashboard/sc`)
      .then(r => setScStats(r.data))
      .catch(() => {})
      .finally(() => setScLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // API and api are stable module-level constants — safe to omit

  const daysSince = (iso) => {
    if (!iso) return 0;
    return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
  };

  const kpis = [
    { title:"Total Jobs",      value: stats?.total_jobs    ?? 0,  icon: ClipboardList, iconBg:"bg-slate-100 text-slate-600",   testId:"dashboard-kpi-total-jobs" },
    { title:"Active Pickups",  value: stats?.active_pickup ?? 0,  icon: Truck,         iconBg:"bg-violet-50 text-violet-600",  testId:"dashboard-kpi-in-repair"  },
    { title:"At Service Ctr",  value: stats?.at_center     ?? 0,  icon: ClipboardList, iconBg:"bg-sky-50 text-sky-600",        testId:"dashboard-kpi-received"   },
    { title:"Repairing",       value: stats?.repairing     ?? 0,  icon: Wrench,        iconBg:"bg-amber-50 text-amber-600",    testId:"dashboard-kpi-completed"  },
    { title:"In Dispatch",     value: stats?.dispatching   ?? 0,  icon: TrendingUp,    iconBg:"bg-orange-50 text-orange-600",  testId:"dashboard-kpi-delivered"  },
    { title:"Today's Jobs",    value: stats?.today_jobs    ?? 0,  icon: Calendar,      iconBg:"bg-purple-50 text-purple-600", testId:"dashboard-kpi-today-jobs" },
    { title:"Total Revenue",   value:`\u20b9${(stats?.total_revenue??0).toLocaleString("en-IN")}`, icon:TrendingUp, iconBg:"bg-green-50 text-green-600", testId:"dashboard-kpi-revenue" },
    { title:"Amount Due",      value:`\u20b9${(stats?.total_due??0).toLocaleString("en-IN")}`,     icon:DollarSign, iconBg:"bg-rose-50 text-rose-600",   testId:"dashboard-kpi-due"     },
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" style={{fontFamily:"'Space Grotesk',sans-serif"}}>Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">XSTRONG POWER EV CHARGER &#8212; Service Center Overview</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/scan">
            <Button variant="outline" size="sm" className="h-8 text-xs bg-slate-900 border-slate-800 text-white hover:bg-slate-800 font-semibold">
              <ScanLine size={13} className="mr-1.5" /> Scan Barcode
            </Button>
          </Link>
          <a href="/qr-poster" target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" className="h-8 text-xs bg-green-50 border-green-300 text-green-700 hover:bg-green-100 font-semibold">
              🖨️ Print QR Poster
            </Button>
          </a>
          <a href="/request" target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" className="h-8 text-xs border-green-200 text-green-700 hover:bg-green-50">
              <ExternalLink size={13} className="mr-1.5"/> Customer Request Form
            </Button>
          </a>
          <a href="/track" target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" className="h-8 text-xs text-slate-500 hover:bg-slate-50">
              <ExternalLink size={13} className="mr-1.5"/> Tracking Portal
            </Button>
          </a>
          <Link to="/jobs/new">
            <Button className="bg-green-600 hover:bg-green-700 text-white" data-testid="dashboard-new-job-button">
              <PlusCircle className="w-4 h-4 mr-2"/> New Service Job
            </Button>
          </Link>
        </div>
      </div>

      {error && <div className="flex items-center gap-2 p-3 bg-rose-50 border border-rose-200 rounded-xl mb-6 text-sm text-rose-700"><AlertCircle size={15}/>{error}</div>}

      {/* Overdue Repairs Alert */}
      {overdue.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-2xl p-4 mb-6" data-testid="dashboard-overdue-alert">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-xl bg-amber-500 flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={15} className="text-white"/>
            </div>
            <div>
              <p className="text-sm font-bold text-amber-900" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
                ⚠️ {overdue.length} Job{overdue.length !== 1 ? "s" : ""} Overdue in Repair (&gt;{overdueConfig} day{overdueConfig !== 1 ? "s" : ""})
              </p>
              <p className="text-xs text-amber-700">These jobs need immediate attention — configure threshold in Settings</p>
            </div>
          </div>
          <div className="space-y-1.5">
            {overdue.map(j => (
              <Link key={j.id} to={`/jobs/${j.id}`}
                className="flex items-center gap-3 text-xs text-amber-800 hover:text-amber-900 hover:bg-amber-100 p-2 rounded-lg transition-colors group">
                <Clock size={12} className="text-amber-500 flex-shrink-0"/>
                <span className="font-mono font-bold">{j.job_no}</span>
                <span className="text-amber-700">{j.customer_name}</span>
                {j.technician && <span className="text-amber-500 ml-auto">{j.technician}</span>}
                <span className="text-amber-500 ml-auto group-hover:text-amber-700">
                  {daysSince(j.since)} day{daysSince(j.since) !== 1 ? "s" : ""} in repair →
                </span>
              </Link>
            ))}
          </div>
          <Link to="/jobs?status=Repairing">
            <Button variant="outline" size="sm" className="mt-3 h-7 text-xs text-amber-700 border-amber-300 hover:bg-amber-100">
              View all repairing jobs →
            </Button>
          </Link>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {kpis.map(k => <KPICard key={k.title} {...k} loading={loading}/>)}
      </div>

      {/* ── Service Centre Card ─────────────────────────────────────────── */}
      <Card className="rounded-2xl border border-slate-200/70 bg-white mb-8 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-green-600 flex items-center justify-center flex-shrink-0">
              <Wrench size={15} className="text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-slate-900" style={{fontFamily:"'Space Grotesk',sans-serif"}}>
                Service Centre
              </h2>
              <p className="text-xs text-slate-400">Walk-in / direct intake jobs</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/sc/new">
              <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white">
                <Plus size={11} className="mr-1" /> New SC Job
              </Button>
            </Link>
            <Link to="/sc">
              <Button variant="ghost" size="sm" className="text-green-600 text-xs h-7">
                View all <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
        <CardContent className="p-0">
          <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-slate-100">
            {[
              { label:"Today's Jobs",    value: scStats?.today_sc   ?? 0, color:"text-green-600",   bg:"bg-green-50",   dot:"bg-green-500" },
              { label:"Pending Repairs", value: scStats?.pending_sc ?? 0, color:"text-amber-600",   bg:"bg-amber-50",   dot:"bg-amber-500" },
              { label:"Received",        value: scStats?.received_sc?? 0, color:"text-sky-600",     bg:"bg-sky-50",     dot:"bg-sky-500"   },
              { label:"Repairing",       value: scStats?.repairing_sc??0, color:"text-orange-600",  bg:"bg-orange-50",  dot:"bg-orange-500"},
            ].map((stat, i) => (
              <div key={stat.label} className={`px-5 py-4 ${i === 0 ? "sm:rounded-bl-2xl" : ""}`}>
                <div className="flex items-center gap-1.5 mb-2">
                  <span className={`w-2 h-2 rounded-full ${stat.dot}`} />
                  <p className="text-xs text-slate-500">{stat.label}</p>
                </div>
                {scLoading
                  ? <Skeleton className="h-7 w-12" />
                  : <p className={`text-2xl font-semibold tabular-nums ${stat.color}`}
                      style={{fontFamily:"'Space Grotesk',sans-serif"}}>
                      {stat.value}
                    </p>}
              </div>
            ))}
          </div>
          {/* Quick links */}
          <div className="flex items-center gap-2 px-5 py-2.5 bg-slate-50/60 border-t border-slate-100 flex-wrap">
            {[
              { label:"Today's SC jobs",  to:"/sc",              dot:"bg-green-500" },
              { label:"Pending repairs",  to:"/sc",              dot:"bg-amber-500" },
              { label:"All SC jobs",      to:"/sc",              dot:"bg-slate-400"  },
            ].map(link => (
              <Link key={link.label} to={link.to}
                className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-green-700 transition-colors">
                <span className={`w-1.5 h-1.5 rounded-full ${link.dot}`} />
                {link.label} →
              </Link>
            ))}
          </div>

          {/* Overdue SC Alert */}
          {!scLoading && (scStats?.overdue_sc || []).length > 0 && (
            <div className="border-t border-amber-200 bg-amber-50 px-5 py-3">
              <p className="text-xs font-bold text-amber-800 flex items-center gap-1.5 mb-2">
                <AlertTriangle size={12} className="text-amber-600" />
                {scStats.overdue_sc.length} SC Job{scStats.overdue_sc.length !== 1 ? "s" : ""} overdue in Received / Repairing (&gt;{scStats.overdue_days || 3} day{(scStats.overdue_days || 3) !== 1 ? "s" : ""})
              </p>
              <div className="space-y-1">
                {scStats.overdue_sc.slice(0, 5).map(j => (
                  <Link key={j.id} to={`/sc/${j.id}`}
                    className="flex items-center gap-3 text-xs text-amber-800 hover:bg-amber-100 p-1.5 rounded-lg transition-colors group">
                    <Clock size={10} className="text-amber-500 flex-shrink-0" />
                    <span className="font-mono font-bold">{j.sc_no}</span>
                    <span className="text-amber-700">{j.customer?.name}</span>
                    <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full font-medium ${j.current_status === "Repairing" ? "bg-orange-100 text-orange-700" : "bg-sky-100 text-sky-700"}`}>
                      {j.current_status}
                    </span>
                  </Link>
                ))}
              </div>
              {scStats.overdue_sc.length > 5 && (
                <Link to="/sc" className="text-xs text-amber-600 hover:text-amber-800 mt-1 block">
                  +{scStats.overdue_sc.length - 5} more →
                </Link>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-2xl border border-slate-200/70 bg-white">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-900" style={{fontFamily:"'Space Grotesk',sans-serif"}}>Recent Jobs</h2>
          <Link to="/jobs"><Button variant="ghost" size="sm" className="text-green-600 text-xs h-7">View all <ArrowRight className="w-3 h-3 ml-1"/></Button></Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="dashboard-recent-jobs-table">
            <thead>
              <tr className="border-b border-slate-100">
                {["Job #","Customer","Product","Status","Payment","Date",""].map(h=>(
                  <th key={h} className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? ([...Array(3)].map((_,i) => (
                <tr key={i}>{[...Array(7)].map((_,j)=><td key={j} className="px-4 py-3"><Skeleton className="h-4"/></td>)}</tr>
              ))) : stats?.recent_jobs?.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <ClipboardList className="w-8 h-8 text-slate-300"/>
                    <p className="text-sm text-slate-500">No jobs yet.</p>
                    <Link to="/jobs/new"><Button size="sm" className="bg-green-600 hover:bg-green-700 text-white">Create first job</Button></Link>
                  </div>
                </td></tr>
              ) : stats?.recent_jobs?.map(job=>(
                <tr key={job.id} className="border-b border-slate-50 hover:bg-slate-50/70 transition-colors" data-testid="dashboard-recent-jobs-row">
                  <td className="px-4 py-3"><span className="font-mono text-sm font-semibold text-green-700">{job.job_no}</span></td>
                  <td className="px-4 py-3"><p className="text-sm font-medium text-slate-900">{job.customer?.name}</p><p className="text-xs text-slate-500">{job.customer?.phone}</p></td>
                  <td className="px-4 py-3"><p className="text-sm text-slate-700">{job.product?.product_type}</p><p className="text-xs font-mono text-slate-500">{job.product?.model}</p></td>
                  <td className="px-4 py-3"><StatusBadge status={job.current_status}/></td>
                  <td className="px-4 py-3"><PayBadge s={job.payment?.payment_status}/></td>
                  <td className="px-4 py-3"><p className="text-xs text-slate-500">{fmtDate(job.created_at)}</p></td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/jobs/${job.id}`}><Button variant="ghost" size="sm" className="text-green-600 h-7 text-xs" data-testid="dashboard-recent-jobs-row">View</Button></Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
